<template>
  <div class="chart-container">
    <div v-if="size !== 'mini'" class="chart-legend-container" style="height: 50px;">
      <label style="margin-left: 10px;">
        <span class="chart-mark chart-mark-y" :style="{ backgroundColor: colorSet.blue.pointBackgroundColor }"></span>
        <span class="chart-legend-name">{{yActualLabel}}</span>
      </label>
      <label style="margin-left: 10px;">
        <span class="chart-mark chart-mark-y" :style="{ backgroundColor: colorSet.red.pointBackgroundColor }"></span>
        <span class="chart-legend-name">{{yPredLabel}}</span>
      </label>
    </div>
    <div class="chart-main-container">
      <chart ref="chart" chartType="line" chartId="LiftCompareChart" :width="size === 'mini' ? 300 : 836"
             :height="size === 'mini' ? 180 : 400"></chart>
    </div>
    <div class="chart-column-container" v-if="liftCompareComparison.results.length > 1 && size !== 'mini'">
      <el-radio v-for="(result, index) in liftCompareComparison.results" v-model="selectedColumnIndex"
                name="lift-compare-column" :label="index">{{ result.name }}
      </el-radio>
    </div>
  </div>
</template>

<script lang="js">
import { GraphLineColorSet } from '../../defines/chart-color'
import ChartCmp from './base.vue'
import { Category } from '../../defines/const'
import CommonUtil from '../../utils/common'
import FormatUtil from '../../utils/format'

export default {
  name: 'LiftCompareChartCmp',
  components: {
    'chart': ChartCmp
  },
  props: {
    liftCompareComparison: {type: Object, default: () => {}},
    yName: '',
    categoryId: '',
    size: '',
    initialColumnIndex: {type: Number, default: 0}
  },
  data () {
    return {
      selectedColumnIndex: -1,
      selectedLiftCompareResult: null,
      colorSet: GraphLineColorSet,
      yActualLabel: '',
      yPredLabel: ''
    }
  },
  created () {
    this.selectedColumnIndex = this.initialColumnIndex
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  },
  watch: {
    selectedColumnIndex (newIndex) {
      if (newIndex < 0) return
      this.selectedLiftCompareResult = this.liftCompareComparison.results[newIndex]
    },
    selectedLiftCompareResult (liftCompareResult) {
      if (!liftCompareResult) return

      const categoryId = this.categoryId
      const yLabel = categoryId === Category.CLF
        ? `「${this.yName} = ${this.selectedLiftCompareResult.name}」の発生率`
        : `「${this.yName}」の平均値`
      const yActualLabel = categoryId === Category.CLF
        ? `「${this.yName} = ${this.selectedLiftCompareResult.name}」の発生率の実績`
        : `「${this.yName}」の平均値の実績`
      const yPredLabel = categoryId === Category.CLF
        ? `「${this.yName} = ${this.selectedLiftCompareResult.name}」の発生率の予測`
        : `「${this.yName}」の平均値の予測`
      const xLabel = categoryId === Category.CLF ? `予測確率` : `予測値`
      const yActual = liftCompareResult.value.map(value => value.yActual)
      const yPred = liftCompareResult.value.map(value => value.yPred)
      const order = liftCompareResult.value.map(value => value.order)
      const isMini = this.size === 'mini'

      this.yActualLabel = yActualLabel
      this.yPredLabel = yPredLabel

      let yActualFraction = 0
      if (categoryId === Category.CLF) {
        if (yActual.length > 1) {
          const minValue = yActual.reduce((a, b) => a < b ? a : b)
          const maxValue = yActual.reduce((a, b) => a > b ? a : b)
          let diffValue = Math.abs(maxValue - minValue)
          while (diffValue !== 0 && diffValue < 0.1) {
            yActualFraction++
            diffValue *= 10
          }
        }
      }
      let yPredFraction = 0
      if (categoryId === Category.CLF) {
        if (yPred.length > 1) {
          const minValue = yPred.reduce((a, b) => a < b ? a : b)
          const maxValue = yPred.reduce((a, b) => a > b ? a : b)
          let diffValue = Math.abs(maxValue - minValue)
          while (diffValue !== 0 && diffValue < 0.1) {
            yPredFraction++
            diffValue *= 10
          }
        }
      }

      const fraction = Math.max(yActualFraction, yPredFraction)

      let datasets = []
      datasets.push(
        Object.assign(
          {
            label: yActualLabel,
            fill: false,
            data: yActual,
            lineTension: 0,
            pointRadius: isMini ? 0 : undefined,
            pointBorderWidth: isMini ? 0 : 8
          },
          this.colorSet.blue
        )
      )
      datasets.push(
        Object.assign(
          {
            label: yPredLabel,
            fill: false,
            data: yPred,
            lineTension: 0,
            pointRadius: isMini ? 0 : undefined,
            pointBorderWidth: isMini ? 0 : 8
          },
          this.colorSet.red
        )
      )

      const chartData = {
        labels: order,
        datasets: datasets
      }
      const options = {
        responsive: false,
        title: {
          display: false
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              id: 'y',
              position: 'left',
              scaleLabel: {
                display: !isMini,
                labelString: yLabel,
                fontSize: 16
              },
              ticks: {
                major: {},
                minor: {},
                callback: function (value, index, values) {
                  if (CommonUtil.isEmpty(value)) {
                    return ''
                  }

                  if (categoryId === Category.CLF) {
                    return FormatUtil.percentage(value, fraction) + '%'
                  } else {
                    return value
                  }
                }
              }
            }
          ],
          xAxes: [
            {
              scaleLabel: {
                display: !isMini,
                labelString: '低 ← ' + xLabel + '順 → 高',
                fontSize: 16
              },
              ticks: {
                maxRotation: 90
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetIndex = tooltipItem.datasetIndex
              let label = data.datasets[datasetIndex].label || ''

              if (CommonUtil.isNotEmpty(label)) {
                label += ': '
              }
              if (categoryId !== Category.CLF) {
                label += FormatUtil.formatNumber(tooltipItem.yLabel)
              } else {
                label += FormatUtil.percentage(tooltipItem.yLabel, fraction) + '%'
              }

              return label
            }
          }
        }
      }

      this.$refs.chart.renderChart(chartData, options)
    }
  }
}

</script>

<style scoped>
  .chart-container {
    display: flex;
    flex-direction: column;
  }

  .chart-legend-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .chart-legend-container > label {
    display: flex;
    align-items: center;
  }

  .chart-mark {
    display: inline-block;
    width: 12px;
    height: 12px;
  }

  .chart-mark-y {
    border-radius: 50%;
    margin-right: 10px;
  }

  .chart-legend-name {
    margin-right: 10px;
  }

  .chart-main-container {
    display: flex;
    justify-content: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/chart/lift-compare.vue